import apiClient from '../utils/apiClient.ts'

/**
 * Category entity from OCAPI Data API.
 */
export interface Category {
    readonly id: string
    readonly name?: string
    readonly description?: string
    readonly parent_category_id?: string
    readonly online?: boolean
    readonly page_title?: string
    readonly page_description?: string
    readonly thumbnail?: string
    readonly image?: string
}

/**
 * Paginated response wrapper from OCAPI.
 */
export interface CategoriesResponse {
    readonly count: number
    readonly start: number
    readonly total: number
    readonly data: Prettify<Category>[]
}

/**
 * Category search response from OCAPI.
 * Note: hits contains category objects directly (not wrapped).
 */
export interface CategorySearchResponse {
    readonly count: number
    readonly start: number
    readonly total: number
    readonly hits: Prettify<Category>[]
}

/**
 * Query parameters for fetching categories.
 */
export interface GetCategoriesParams {
    readonly start?: number
    readonly count?: number
    readonly select?: string
    readonly expand?: string[]
}

/**
 * Request body for searching categories.
 */
interface SearchCategoriesRequest {
    readonly query: {
        readonly text_query: {
            readonly fields: string[]
            readonly search_phrase: string
        }
    }
    readonly start?: number
    readonly count?: number
    readonly select?: string
}

/**
 * Fetch all categories for a catalog.
 *
 * GET /catalogs/{catalog_id}/categories
 */
export async function getCategories(
    catalogId: string,
    params?: GetCategoriesParams
): Promise<CategoriesResponse> {
    const response = await apiClient.get<CategoriesResponse>(
        `/catalogs/${encodeURIComponent(catalogId)}/categories`,
        { params }
    )
    return response.data
}

/**
 * Search categories by query string.
 *
 * POST /catalogs/{catalog_id}/category_search
 */
export async function searchCategories(
    catalogId: string,
    searchPhrase: string,
    options?: {
        readonly start?: number
        readonly count?: number
        readonly select?: string
    },
    signal?: AbortSignal
): Promise<CategorySearchResponse> {
    const requestBody = {
        query: {
            text_query: {
                fields: ['id', 'name'],
                search_phrase: searchPhrase
            }
        },
        start: options?.start,
        count: options?.count,
        select: options?.select
    } satisfies SearchCategoriesRequest

    const response = await apiClient.post<CategorySearchResponse>(
        `/catalogs/${encodeURIComponent(catalogId)}/category_search`,
        requestBody,
        { signal }
    )
    return response.data
}
